import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContentPanel from './components/ContentPanel';
import ComplianceAnalysis from './components/ComplianceAnalysis';
import ApprovalWorkflow from './components/ApprovalWorkflow';
import RegulatoryLookup from './components/RegulatoryLookup';
import SimilarContentComparison from './components/SimilarContentComparison';

const ContentReviewDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('analysis');
  const [isLoading, setIsLoading] = useState(true);

  // Mock current user data
  const currentUser = {
    name: "Emily Rodriguez",
    role: "Senior Compliance Officer",
    id: "user-001"
  };

  // Mock content data
  const contentData = {
    id: "content-2024-09-20-001",
    title: "Q4 High-Yield Savings Promotion",
    platform: "Facebook",
    author: "Marketing Team",
    campaign: "Q4 Savings Drive",
    scheduledDate: "2024-09-25T10:00:00Z",
    text: `🏦 Supercharge your savings this quarter! \n\nOur NEW Premium Savings Account offers GUARANTEED returns up to 5.5% APY! \n\n✅ No minimum balance required\n✅ Instant access to your money\n✅ FDIC protected up to $500,000\n\n💰 Limited time offer - rates this high won't last forever!\n\nOpen your account TODAY and watch your money grow! 🚀\n\n#HighYieldSavings #BankingDeals #SaveMore`,
    media: [
      {
        name: "savings-promo-banner.jpg",
        type: "image",
        size: "2.1 MB"
      },
      {
        name: "testimonial-video.mp4",
        type: "video",
        size: "15.3 MB"
      }
    ],
    hashtags: ["HighYieldSavings", "BankingDeals", "SaveMore"]
  };

  // Mock compliance analysis data
  const complianceAnalysis = {
    overallRisk: "High",
    complianceScore: 45,
    violationCounts: {
      critical: 2,
      high: 3,
      medium: 1
    },
    violations: [
      {
        id: "violation-001",
        rule: "FDIC Insurance Misrepresentation",
        severity: "Critical",
        regulation: "FDIC Part 328.3",
        flaggedText: "FDIC protected up to $500,000",
        description: "Incorrect FDIC insurance coverage amount stated",
        explanation: "FDIC insurance covers deposits up to $250,000 per depositor, per insured bank, for each account ownership category. The stated amount of $500,000 is misleading and violates FDIC advertising guidelines.",
        citation: "12 CFR 328.3(c) - No insured depository institution may use the terms 'guaranteed' or 'insured' in a misleading manner in any advertisement.",
        suggestions: [
          {
            text: "FDIC insured up to $250,000 per depositor",
            reason: "Corrects the insurance amount to the actual FDIC limit"
          },
          {
            text: "Deposits protected by FDIC insurance up to applicable limits",
            reason: "General statement that avoids specific incorrect amounts"
          }
        ]
      },
      {
        id: "violation-002",
        rule: "Misleading Rate Guarantees",
        severity: "Critical",
        regulation: "CFPB Truth in Savings",
        flaggedText: "GUARANTEED returns up to 5.5% APY",
        description: "Use of \'guaranteed\' with variable rate products is prohibited",
        explanation: "Savings account rates are variable and subject to change. Using \'guaranteed\' creates false expectations and violates truth in savings regulations.",
        citation: "12 CFR 1030.8(a) - Advertisements must not be misleading or inaccurate or misrepresent the deposit contract.",
        suggestions: [
          {
            text: "Earn up to 5.5% APY*",
            reason: "Removes guarantee language and adds asterisk for disclosures"
          },
          {
            text: "Current rate up to 5.5% APY, subject to change",
            reason: "Clearly indicates rate variability"
          }
        ]
      },
      {
        id: "violation-003",
        rule: "Missing Rate Disclosures",
        severity: "High",
        regulation: "CFPB Regulation DD",
        flaggedText: "up to 5.5% APY",
        description: "APY advertisement missing required disclosures",
        explanation: "When advertising APY, institutions must include the minimum balance required to earn the advertised rate and other material terms.",
        citation: "12 CFR 1030.8(c) - If an advertisement states a rate of return, it must state the rate as an 'annual percentage yield.'",
        suggestions: [
          {
            text: "up to 5.5% APY* (*Rate applies to balances of $10,000 or more. Lower rates apply to smaller balances.)",
            reason: "Includes balance requirements for advertised rate"
          }
        ]
      },
      {
        id: "violation-004",
        rule: "Promotional Language Restrictions",
        severity: "High",
        regulation: "FTC Act Section 5",
        flaggedText: "rates this high won\'t last forever",
        description: "Creates false urgency without specific terms",
        explanation: "Promotional language that creates artificial urgency without clear end dates or terms can be considered deceptive advertising.",
        citation: "15 USC 45 - Unfair or deceptive acts or practices in commerce are unlawful.",
        suggestions: [
          {
            text: "Promotional rate valid through December 31, 2024",
            reason: "Provides specific end date for promotional offer"
          }
        ]
      },
      {
        id: "violation-005",
        rule: "Minimum Balance Disclosure",
        severity: "High",
        regulation: "CFPB Regulation DD",
        flaggedText: "No minimum balance required",
        description: "Contradicts typical high-yield savings requirements",
        explanation: "Most high-yield savings accounts offering premium rates require minimum balances. This statement may be misleading if not accurate.",
        citation: "12 CFR 1030.8(a) - Advertisements must not be misleading about account terms.",
        suggestions: [
          {
            text: "No minimum balance to open account*",
            reason: "Clarifies that no minimum applies to opening, not maintaining rates"
          }
        ]
      },
      {
        id: "violation-006",
        rule: "Social Media Compliance",
        severity: "Medium",
        regulation: "FINRA Social Media Guidelines",
        flaggedText: "watch your money grow! 🚀",
        description: "Overly promotional language with growth implications",
        explanation: "Social media posts should avoid language that implies guaranteed growth or returns, especially with celebratory emojis that may mislead consumers.",
        citation: "FINRA Rule 2210 - Communications with the public must be fair and balanced.",
        suggestions: [
          {
            text: "start earning competitive rates on your savings",
            reason: "More conservative language without growth implications"
          }
        ]
      }
    ]
  };

  // Mock workflow data
  const workflowData = {
    currentStatus: "Pending",
    priority: "High",
    assignedTo: "Emily Rodriguez",
    dueDate: "2024-09-22T17:00:00Z",
    history: [
      {
        reviewer: "AI System",
        action: "Flagged",
        comment: "Multiple critical compliance violations detected. Requires immediate review.",
        timestamp: "2024-09-20T14:30:00Z"
      },
      {
        reviewer: "Sarah Johnson",
        action: "Assigned",
        comment: "Escalated to senior compliance officer due to FDIC violation severity.",
        timestamp: "2024-09-20T14:45:00Z"
      }
    ],
    versions: [
      {
        number: 1,
        timestamp: "2024-09-20T14:15:00Z",
        current: true
      }
    ],
    activeReviewers: [
      {
        name: "Emily Rodriguez",
        status: "active"
      },
      {
        name: "Michael Chen",
        status: "viewing"
      }
    ]
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/content-analysis-dashboard' },
    { label: 'Content Review', href: '/content-review-detail' },
    { label: contentData?.title }
  ];

  const tabs = [
    { id: 'analysis', label: 'Compliance Analysis', icon: 'AlertTriangle', count: complianceAnalysis?.violations?.length },
    { id: 'workflow', label: 'Approval Workflow', icon: 'GitBranch' },
    { id: 'lookup', label: 'Regulatory Lookup', icon: 'BookOpen' },
    { id: 'similar', label: 'Similar Content', icon: 'GitCompare' }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleContentUpdate = (contentId) => {
    console.log('Edit content:', contentId);
    // Handle content editing
  };

  const handleAcceptSuggestion = (violationId, suggestionIndex) => {
    console.log('Accept suggestion:', violationId, suggestionIndex);
    // Handle suggestion acceptance
  };

  const handleRejectSuggestion = (violationId, suggestionIndex) => {
    console.log('Reject suggestion:', violationId, suggestionIndex);
    // Handle suggestion rejection
  };

  const handleApprove = (comment) => {
    console.log('Approve content with comment:', comment);
    // Handle approval
  };

  const handleReject = (comment) => {
    console.log('Reject content with comment:', comment);
    // Handle rejection
  };

  const handleRequestChanges = (comment) => {
    console.log('Request changes with comment:', comment);
    // Handle change request
  };

  const handleEscalate = (comment) => {
    console.log('Escalate content with comment:', comment);
    // Handle escalation
  };

  const handleRuleSelect = (rule) => {
    console.log('Selected rule:', rule);
    // Handle rule selection
  };

  const handleCompareSelect = (content) => {
    console.log('Compare with content:', content);
    // Handle content comparison
  };

  const handleBackToDashboard = () => {
    navigate('/content-analysis-dashboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} />
        <div className="pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading content review...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} />
      <div className="pt-16">
        <div className="p-6 border-b border-border bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Breadcrumb items={breadcrumbItems} className="mb-2" />
                <h1 className="text-2xl font-semibold text-foreground">Content Review Detail</h1>
                <p className="text-muted-foreground">
                  Analyze flagged content and make informed approval decisions
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={handleBackToDashboard}
                >
                  Back to Dashboard
                </Button>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
                </div>
              </div>
            </div>

            {/* Status Bar */}
            <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
                <div>
                  <div className="font-medium text-red-800">High Risk Content Detected</div>
                  <div className="text-sm text-red-600">
                    {complianceAnalysis?.violations?.length} violations found • Compliance Score: {complianceAnalysis?.complianceScore}/100
                  </div>
                </div>
              </div>
              <div className="text-sm text-red-600">
                Due: {new Date(workflowData.dueDate)?.toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-300px)]">
            {/* Left Panel - Content */}
            <div className="lg:col-span-1">
              <ContentPanel 
                content={contentData}
                onContentUpdate={handleContentUpdate}
              />
            </div>

            {/* Center Panel - Analysis Tabs */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg h-full flex flex-col">
                {/* Tab Navigation */}
                <div className="border-b border-border p-4">
                  <div className="flex space-x-1 bg-muted rounded-lg p-1">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro min-touch-target ${
                          activeTab === tab?.id
                            ? 'bg-card text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon name={tab?.icon} size={16} />
                        <span className="hidden sm:inline">{tab?.label}</span>
                        {tab?.count && (
                          <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                            {tab?.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-hidden">
                  {activeTab === 'analysis' && (
                    <ComplianceAnalysis
                      analysis={complianceAnalysis}
                      onAcceptSuggestion={handleAcceptSuggestion}
                      onRejectSuggestion={handleRejectSuggestion}
                    />
                  )}
                  {activeTab === 'workflow' && (
                    <ApprovalWorkflow
                      workflow={workflowData}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onRequestChanges={handleRequestChanges}
                      onEscalate={handleEscalate}
                    />
                  )}
                  {activeTab === 'lookup' && (
                    <RegulatoryLookup onRuleSelect={handleRuleSelect} />
                  )}
                  {activeTab === 'similar' && (
                    <SimilarContentComparison
                      currentContent={contentData}
                      onCompareSelect={handleCompareSelect}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Workflow */}
            <div className="lg:col-span-1">
              <ApprovalWorkflow
                workflow={workflowData}
                onApprove={handleApprove}
                onReject={handleReject}
                onRequestChanges={handleRequestChanges}
                onEscalate={handleEscalate}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentReviewDetail;